/**
 * WorkflowTrail — the analysis breadcrumb above the object title
 * (doc 147 §5.3 PR D · world-class redesign).
 *
 * Renders the session-scoped trail of `{kind, id}` pairs the user has
 * walked through their analysis, across all 9 catalogued object kinds.
 * Each step except the tail is a button that jumps back to that object.
 * Each step shows its kind's `KIND_META` icon.
 * Hidden when the trail is shorter than 2.
 */
import type { JSX } from 'react';
import { Icon } from '../components/Icon.js';
import { KIND_META } from '../state/objectKinds.js';
import type { TrailEntry } from '../state/workflowTrail.js';

export interface WorkflowTrailProps {
  trail: readonly TrailEntry[];
  onJump: (entry: TrailEntry) => void;
}

export function WorkflowTrail({ trail, onJump }: WorkflowTrailProps): JSX.Element | null {
  if (trail.length < 2) return null;
  const head = trail.slice(0, -1);
  const tail = trail[trail.length - 1]!;
  return (
    <nav className="workflow-trail" aria-label="Workflow trail">
      {head.map((entry, i) => (
        <span key={`${entry.kind}-${entry.id}-${i}`} className="workflow-trail-segment">
          <button
            type="button"
            className="workflow-trail-step"
            onClick={() => onJump(entry)}
            aria-label={`Jump back to ${entry.id}`}
          >
            <Icon name={KIND_META[entry.kind].icon} size="sm" label={KIND_META[entry.kind].label} />
            {entry.id}
          </button>
          <Icon name="chevron_right" size="sm" />
        </span>
      ))}
      <span className="workflow-trail-tail" aria-current="page">
        <Icon name={KIND_META[tail.kind].icon} size="sm" label={KIND_META[tail.kind].label} />
        {tail.id}
      </span>
    </nav>
  );
}
